import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { productValidator } from '@eduzz-product-manager/validators';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { FormBlock } from '@/components/FormBlock';
import { useFormGroupListener } from '@/hooks/useFormGroupListener';
import { useZustandSync } from '@/hooks/useZustandSync';
import useAIProductExampleStore from '@/stores/AIProductExampleStore';
import useProductStore, { productStore } from '@/stores/productStore';

import { TextArea } from '../form/Textarea';
import { type Event } from '../FormGroup';
import { NiceActions } from '../NiceActions';

const productDescriptionSchema = z.object({
  description: productValidator.shape.description
});

type ProductDescriptionFormValues = z.infer<typeof productDescriptionSchema>;

const FIELD_NAME = 'description';
const DESCRIPTION_MAX_LENGTH = 1000;

export default function ProductDescription() {
  const store = productStore.getState();

  const form = useForm<ProductDescriptionFormValues>({
    resolver: zodResolver(productDescriptionSchema),
    mode: 'onChange',
    defaultValues: { [FIELD_NAME]: useProductStore(d => d[FIELD_NAME]) }
  });

  useZustandSync<ProductDescriptionFormValues>(form.control, data => ({
    [FIELD_NAME]: data[FIELD_NAME]
  }));

  const initialValue = useRef(form.control._defaultValues[FIELD_NAME]);
  const productDescription = useProductStore(d => d[FIELD_NAME]);
  const aiProductExampleStore = useAIProductExampleStore(d => d);
  const showIaButton = aiProductExampleStore.hasData();

  useFormGroupListener(FIELD_NAME, async (event: Event) => {
    if (event.type === 'validate') {
      return true;
    }

    if (event.type === 'error' && event.payload?.name === FIELD_NAME) {
      form.setError(FIELD_NAME, { message: event.payload.value });
      return true;
    }
  });

  useEffect(() => {
    if (productDescription === initialValue.current) {
      return;
    }

    store.setIaField('product-description', false);
  }, [productDescription, store]);

  return (
    <FormBlock
      name='product-description'
      form={form}
      title='Descrição'
      lengthIndicator={`${productDescription.length}/${DESCRIPTION_MAX_LENGTH}`}
      preview={<>{productDescription}</>}
    >
      <TextArea
        maxLength={DESCRIPTION_MAX_LENGTH}
        id='product-description'
        placeholder='Descrição do seu produto'
        name={FIELD_NAME}
      />
      {showIaButton && (
        <NiceActions
          onAIGenerate={() => {
            const example = aiProductExampleStore.getExampleOf('description');

            if (example) {
              initialValue.current = example;
              form.setValue(FIELD_NAME, example);
              form.clearErrors(FIELD_NAME);
              store.setIaField('product-description', true);
            }
          }}
        />
      )}
    </FormBlock>
  );
}
